import React, { useState } from 'react';
import { Footer, Header, TripleAction, Content, SubNav } from '../../gs-component-right-rail';
import { RadioButton, SingleSelectGroup, ToggleSwitch } from '@xo-union/tk-component-switches';
import { Formik, FastField as FormikField } from 'formik';
import { Field } from '../../gs-component-form';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { Subtle, Body1 } from '@xo-union/tk-ui-typography';
import { compose } from '@xo-union/react-css-modules';
import Icon from '@xo-union/tk-component-icons';
import { Tooltip, TooltipContainer } from '@xo-union/tk-component-tooltips';
import { onSave } from '../helpers/onSave';
import RSVPEmptyState from './RSVPEmptyState';

import styles from './rsvpSettingsRightRail.scss';
import { RSVPSettingsRailProps } from '.';
import { RSVPForm } from './RSVPForm';
import { EventType } from '../EventTypes';
import { RSVPVisualization } from './RSVPVisualization';
import { Household } from '../HouseholdTypes';
import { useWebsiteLayoutContext } from '../../../context/WebsiteLayoutContext';

type Props = Omit<RSVPSettingsRailProps, 'showRail'> & {
  events?: EventType[];
  openEventModal?: (id: string) => void;
  leftButtonOnClick?: () => void;
  showVisualization?: boolean;
  households?: Household[];
};

const EditInPreviewRSVPSettings: React.FC<Props> = ({
  rsvpOnInEvents,
  hasPrivateEvent,
  onClickEditForm,
  onClose,
  guestWedding,
  onSuccess,
  events = [],
  openEventModal,
  leftButtonOnClick,
  showVisualization = false,
  households = [],
}) => {
  const { currentLayout } = useWebsiteLayoutContext();

  const initialRsvpHidden = guestWedding?.rsvpPageHidden ?? false;
  const initialAllowGuestPreview = guestWedding?.allowGuestPreview ?? false;
  const initialIsPrivateRsvp = guestWedding?.isPrivateRsvp ?? false;
  const subNavNames = ['Content', 'Settings'];
  const SUBNAV_CONTENT_INDEX = subNavNames.indexOf('Content');
  const SUBNAV_SETTINGS_INDEX = subNavNames.indexOf('Settings');
  const [activeTabIndex, setActiveTabIndex] = useState(SUBNAV_CONTENT_INDEX);

  const [rsvpPageHidden, setRsvpPageHidden] = useState(initialRsvpHidden.toString());
  const [allowGuestPreview, setAllowGuestPreview] = useState(initialAllowGuestPreview.toString());
  const [isPrivateRsvp, setIsPrivateRsvp] = useState(initialIsPrivateRsvp.toString());
  const [showTooltip, setShowTooltip] = useState(false);

  const renderContent = (): JSX.Element => {
    if (showVisualization) {
      return <RSVPVisualization isPrivateRsvp={isPrivateRsvp === 'true'} events={events} households={households} />;
    }
    return (
      <RSVPForm
        guestWedding={guestWedding}
        events={events}
        openEventModal={openEventModal}
        onClickEditForm={onClickEditForm}
      />
    );
  };

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  const renderPageVisibility = (): JSX.Element => (
    <Content className={`${styles.eipContentSection} ${!rsvpOnInEvents ? styles.disabled : ''}`}>
      <div className={styles.toggle}>
        <Body1 className={styles.bold} as="h2">
          {pageOrSection} details
        </Body1>
        <ToggleSwitch
          checked={rsvpPageHidden === 'false' && rsvpOnInEvents}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setRsvpPageHidden((!e.target.checked).toString());
          }}
          labelDirection="left"
          data-testid="rsvp-visibility-toggle"
          disabled={!rsvpOnInEvents}
        >
          {rsvpPageHidden === 'false' && rsvpOnInEvents ? 'Visible' : 'Hidden'}
        </ToggleSwitch>
      </div>
      {rsvpOnInEvents ? (
        <Subtle>No one can RSVP from your Website until this page is visible</Subtle>
      ) : (
        <div className={styles.subTextWithLink}>
          <Subtle>Heads up! You must have ‘Collect RSVPs’ on for at least one event to make this page visible</Subtle>
          <DisplayButton onClick={onClickEditForm}>
            <Body1>Edit Form</Body1>
          </DisplayButton>
        </div>
      )}
      <Formik
        initialValues={{ title: 'RSVP' }}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={(): void => {}}
      >
        <FormikField name="text" label="Title" component={Field} value="RSVP" disabled />
      </Formik>
    </Content>
  );

  const handleTooltipCTA = (): void => {
    if (rsvpOnInEvents) {
      setActiveTabIndex(SUBNAV_SETTINGS_INDEX);
      setShowTooltip(false);
    } else {
      onClickEditForm();
    }
  };

  if (!guestWedding) {
    return null;
  }

  const subNav = (
    <div className={`${!rsvpOnInEvents ? styles.subnavSettingsDisabled : ''}`}>
      <SubNav
        subNavNames={subNavNames}
        activeTabIndex={activeTabIndex}
        onTabClick={
          rsvpOnInEvents
            ? setActiveTabIndex
            : (): void => {
                setActiveTabIndex(0);
              }
        }
      />
    </div>
  );

  return (
    <>
      <Header
        onClose={onClose}
        as="h1"
        subNav={subNav}
        leftButtonOnClick={leftButtonOnClick}
        leftButtonText={leftButtonOnClick !== undefined ? 'Back' : ''}
      >
        <div className={styles.headerWrap}>
          RSVP{' '}
          {(!rsvpOnInEvents || rsvpPageHidden === 'true') && (
            <TooltipContainer className={styles.tooltipContainer}>
              <Icon
                name="eye_closed"
                data-testid="header-page-hidden-icon"
                size="md"
                className={styles.eyeIcon}
                onMouseEnter={(): void => {
                  setShowTooltip(true);
                }}
              />
              <Tooltip
                arrowPosition="top"
                arrowOffset={25}
                className={`${styles.tip} ${showTooltip ? styles.showTip : ''}`}
                onMouseLeave={(): void => {
                  setShowTooltip(false);
                }}
              >
                <Body1 className={styles.tooltip}>
                  Turn this page on from{' '}
                  <DisplayButton onClick={handleTooltipCTA} color="primary-alternative">
                    RSVP settings
                  </DisplayButton>{' '}
                  so guests can RSVP on your website.
                </Body1>
              </Tooltip>
            </TooltipContainer>
          )}
        </div>
      </Header>

      <Content className={styles.rightRailContent}>
        {activeTabIndex === SUBNAV_CONTENT_INDEX ? (
          <>
            {!rsvpOnInEvents ? <RSVPEmptyState /> : <div className={styles.eipContentSection}>{renderContent()}</div>}
          </>
        ) : (
          <>
            {renderPageVisibility()}
            <Content className={styles.eipContentSection}>
              <Body1 className={styles.contentHeading} as="h2">
                Access
              </Body1>
              <SingleSelectGroup
                value={isPrivateRsvp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  setIsPrivateRsvp(e.target.value);
                }}
              >
                <RadioButton classes={compose({ container: styles.radioButton })} value="true">
                  Guest list only <Subtle>(recommended)</Subtle>
                </RadioButton>
                <Subtle className={styles.subtext}>Only guests on your Guest List can RSVP</Subtle>
                <RadioButton classes={compose({ container: styles.radioButton })} value="false">
                  Public RSVP <Subtle>(not recommended)</Subtle>
                </RadioButton>
                <Subtle className={styles.subtext}>
                  {hasPrivateEvent
                    ? 'Anybody can RSVP for your events, including events that are only open to select guests.'
                    : 'Anyone with the link to your Wedding Website can RSVP.'}
                </Subtle>
              </SingleSelectGroup>
            </Content>
            <Content className={styles.eipContentSection}>
              <Body1 className={styles.contentHeading} as="h2">
                Guest list visibility
              </Body1>
              <SingleSelectGroup
                value={allowGuestPreview}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  setAllowGuestPreview(e.target.value);
                }}
              >
                <RadioButton classes={compose({ container: styles.radioButton })} value="true">
                  Shared guest list
                </RadioButton>
                <Subtle className={styles.subtext}>
                  Allow guests who are attending to see who’s attending each event
                </Subtle>
                <RadioButton classes={compose({ container: styles.radioButton })} value="false">
                  Private guest list
                </RadioButton>
                <Subtle className={styles.subtext}>No guests will be able to see the guest list for any event</Subtle>
              </SingleSelectGroup>
            </Content>
          </>
        )}
      </Content>
      {activeTabIndex !== SUBNAV_CONTENT_INDEX && (
        <Footer>
          <TripleAction
            primaryText="Save"
            primaryOnClick={(): void => {
              onSave(rsvpPageHidden, allowGuestPreview, isPrivateRsvp, onSuccess);
            }}
            secondaryText="Cancel"
            secondaryOnClick={onClose}
          />
        </Footer>
      )}
    </>
  );
};

export default EditInPreviewRSVPSettings;
